import { createClient } from '@supabase/supabase-js';
import { SUPABASE_CONFIG } from './environment';

// Environment-specific Supabase configurations (fallbacks for backward compatibility)
const environments = {
  development: {
    url: SUPABASE_CONFIG.URL,
    key: SUPABASE_CONFIG.ANON_KEY
  },
  staging: {
    url: SUPABASE_CONFIG.URL,
    key: SUPABASE_CONFIG.ANON_KEY
  },
  production: {
    url: SUPABASE_CONFIG.URL,
    key: SUPABASE_CONFIG.ANON_KEY
  }
};

// Function to completely clear all authentication data
export function clearAllAuthData() {
  if (typeof window === 'undefined') return;
  
  // Clear localStorage items
  const keysToRemove = [
    'aerosync-auth-token',
    'sb-aerosync-auth-token',
    'supabase.auth.token',
    'sb-hpozuqhkxpexrzjysvpo-auth-token',
    'sb-hpozuqhkxpexrzjysvpo-auth-token-code-verifier'
  ];
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Clear any other Supabase-related storage
  Object.keys(localStorage).forEach(key => {
    if (key.includes('supabase') || 
        key.includes('auth-token') || 
        key.includes('hpozuqhkxpexrzjysvpo') ||
        key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  
  // Clear sessionStorage as well
  Object.keys(sessionStorage).forEach(key => {
    if (key.includes('supabase') || 
        key.includes('auth-token') || 
        key.includes('hpozuqhkxpexrzjysvpo') ||
        key.includes('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
  
  console.log('✅ Cleared all authentication data');
}

// Global cleanup function for all realtime channels
function cleanupAllChannels() {
  if (!supabaseInstance) return;
  
  try {
    console.log('🧹 [SUPABASE] Cleaning up all realtime channels...');
    
    // Get all channels from Supabase's internal state
    const channels = supabaseInstance.getChannels();
    
    channels.forEach((channel: any) => {
      try {
        console.log(`🔌 [SUPABASE] Removing channel: ${channel.topic}`);
        channel.unsubscribe();
        supabaseInstance.removeChannel(channel);
      } catch (error) {
        console.warn(`⚠️ [SUPABASE] Error cleaning up channel ${channel.topic}:`, error);
      }
    });
    
    console.log(`✅ [SUPABASE] Cleaned up ${channels.length} channels`);
  } catch (error) {
    console.warn('⚠️ [SUPABASE] Error during global channel cleanup:', error);
  }
}

// Get current configuration based on environment or localStorage
function getCurrentSupabaseConfig() {
  // Always use production config - no more environment switching
  return {
    url: SUPABASE_CONFIG.URL,
    key: SUPABASE_CONFIG.ANON_KEY
  };
}

// Singleton pattern to ensure only one Supabase client instance
let supabaseInstance: any = null;

function createSupabaseClient() {
  // Prevent multiple initializations
  if (supabaseInstance) {
    return supabaseInstance;
  }

  const config = getCurrentSupabaseConfig();
  
  // Use the standardized configuration
  const supabaseUrl = config.url;
  const supabaseKey = config.key;

  if (!supabaseUrl || !supabaseKey) {
    console.error('Missing Supabase configuration');
    throw new Error('Missing Supabase configuration');
  }

  const environment = import.meta.env.MODE;
    
  console.log(`Initializing Supabase client for ${environment} environment`);

  // Clear any existing invalid tokens before creating client
  if (typeof window !== 'undefined') {
    try {
      // Check for potentially invalid tokens
      const existingToken = localStorage.getItem('sb-hpozuqhkxpexrzjysvpo-auth-token');
      if (existingToken) {
        try {
          const tokenData = JSON.parse(existingToken);
          const expiresAt = tokenData?.expires_at;
          if (expiresAt && Date.now() >= expiresAt * 1000) {
            console.log('Found expired token, clearing...');
            clearAllAuthData();
          }
        } catch (e) {
          console.log('Found invalid token format, clearing...');
          clearAllAuthData();
        }
      }
    } catch (error) {
      console.warn('Error checking existing tokens:', error);
      clearAllAuthData();
    }
  }

  // Create the Supabase client with optimized settings
  supabaseInstance = createClient(supabaseUrl, supabaseKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`,
      flowType: 'pkce'
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    },
    global: {
      headers: {
        'X-Client-Info': 'aerosync-web@1.0.0'
      }
    },
    db: {
      schema: 'public'
    }
  });

  // Handle authentication state changes and errors
  supabaseInstance.auth.onAuthStateChange(async (event: string, session: any) => {
    console.log('Auth state change:', event);
    
    if (event === 'TOKEN_REFRESHED') {
      console.log('✅ Authentication token refreshed successfully');
    } else if (event === 'SIGNED_OUT') {
      console.log('✅ User signed out');
      clearAllAuthData();
      // Clean up all realtime channels on sign out
      cleanupAllChannels();
    } else if (event === 'SIGNED_IN') {
      console.log('✅ User signed in successfully');
    }
  });

  // Wrap the auth methods to handle errors gracefully
  const originalRefreshSession = supabaseInstance.auth.refreshSession;
  supabaseInstance.auth.refreshSession = async (...args: any[]) => {
    try {
      return await originalRefreshSession.apply(supabaseInstance.auth, args);
    } catch (error: any) {
      if (error?.message?.includes('refresh_token') || 
          error?.message?.includes('Invalid Refresh Token') ||
          error?.message?.includes('Refresh Token Not Found')) {
        console.warn('🔄 Refresh token invalid, clearing auth data...');
        clearAllAuthData();
        return { data: { session: null, user: null }, error: null };
      }
      throw error;
    }
  };

  return supabaseInstance;
}

// Initialize the singleton instance
export const supabase = createSupabaseClient();

// Add global cleanup event listeners
if (typeof window !== 'undefined') {
  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    cleanupAllChannels();
  });
  
  // Cleanup on page visibility change (when tab becomes hidden)
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      cleanupAllChannels();
    }
  });
}

// Utility to get fresh config (for components that need current settings)
export { getCurrentSupabaseConfig };

// Export cleanup function for manual use
export { cleanupAllChannels };

// Function to handle authentication errors and recover
export async function handleAuthError(error: any) {
  if (error?.message?.includes('refresh_token') || 
      error?.message?.includes('Invalid Refresh Token') ||
      error?.message?.includes('Refresh Token Not Found') ||
      error?.message?.includes('JWT expired')) {
    
    console.warn('🚨 Authentication error detected, recovering...');
    clearAllAuthData();
    
    // Sign out to clear the session
    try {
      await supabase.auth.signOut();
    } catch (signOutError) {
      console.warn('Error during sign out:', signOutError);
    }
    
    return true; // Indicates error was handled
  }
  return false; // Error not handled
}

// Function to safely initialize auth
export async function initializeAuth() {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      await handleAuthError(error);
    }
    return data.session;
  } catch (error) {
    console.warn('Error initializing auth:', error);
    await handleAuthError(error);
    return null;
  }
}

// Rate-limited signup function to prevent too many requests
const signupAttempts = new Map();

export async function rateAwareSignUp(email: string, password: string, metadata: any = {}) {
  const now = Date.now();
  const lastAttempt = signupAttempts.get(email) || 0;
  const timeSince = now - lastAttempt;
  
  // Rate limit: 1 signup per email per minute
  if (timeSince < 60000) {
    throw new Error('Please wait before attempting another signup');
  }
  
  signupAttempts.set(email, now);
  
  try {
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    
    return result;
  } catch (error) {
    // Remove from rate limit cache on error to allow retry
    signupAttempts.delete(email);
    throw error;
  }
}

// Export default for backward compatibility
export default supabase;
